import { useState } from 'react'
import { AnchoredOverlay, ConfirmDialog, useToast } from '@orbind/ui'
import { OPCOES_DE_SILENCIO, estaSilenciada, type CanalDeChat, type DuracaoDoSilencio } from '@orbind/core'
import { useAcoesDaLista } from './useChat.ts'

/**
 * Menu do botão direito numa conversa da lista (pedido 2026-09-14, referência:
 * o mensageiro de sempre): arquivar, silenciar, fixar, marcar como não lida,
 * favoritar, limpar histórico, sair do grupo.
 *
 * Fica no lado de quem PEDIU a mudança — nada aqui muda a conversa dos outros
 * participantes (nem "limpar", que só esconde do MEU histórico; nem "sair",
 * que é a mesma regra de sempre).
 *
 * "Adicionar à lista" do print de referência ficou de fora desta rodada —
 * listas próprias (criar, nomear, organizar conversas dentro delas) é uma
 * funcionalidade por si só, maior que um item de menu.
 */
export function MenuDaConversa({
  canal,
  ancora,
  onClose,
  euMemberId,
  estaAberta,
  onSairDaAberta,
}: {
  canal: CanalDeChat
  ancora: HTMLElement
  onClose: () => void
  euMemberId: string
  /** A conversa é a que está aberta agora — sair dela precisa navegar para fora. */
  estaAberta: boolean
  onSairDaAberta: () => void
}) {
  const toast = useToast()
  const acoes = useAcoesDaLista()
  const [submenuSilencio, setSubmenuSilencio] = useState<HTMLElement | null>(null)
  const [confirmando, setConfirmando] = useState<'limpar' | 'sair' | null>(null)

  const avisarFalha = (erro: Error) => toast.push({ tone: 'danger', message: erro.message })
  const fixada = canal.fixadaEm !== null
  const favorita = canal.favoritaEm !== null
  const arquivada = canal.arquivadaEm !== null
  const silenciada = estaSilenciada(canal.silenciadaAte)
  const podeMarcarNaoLida = canal.naoLidas === 0 && !canal.marcadaNaoLida

  const fechar = () => {
    onClose()
    setSubmenuSilencio(null)
  }

  return (
    <>
      <AnchoredOverlay anchor={ancora} onClose={fechar} prefer="direita" role="menu" aria-label="Opções da conversa" className="ch-menu">
        <button
          type="button"
          role="menuitem"
          className="ch-more__item"
          onClick={() => {
            acoes.arquivar.mutate({ channelId: canal.id, arquivar: !arquivada }, { onError: avisarFalha })
            fechar()
          }}
        >
          <i className={arquivada ? 'fas fa-box-open' : 'fas fa-box-archive'} aria-hidden />
          {arquivada ? 'Desarquivar conversa' : 'Arquivar conversa'}
        </button>

        {silenciada ? (
          <button
            type="button"
            role="menuitem"
            className="ch-more__item"
            onClick={() => {
              acoes.silenciar.mutate({ channelId: canal.id, duracao: null }, { onError: avisarFalha })
              fechar()
            }}
          >
            <i className="fas fa-bell" aria-hidden />
            Reativar notificações
          </button>
        ) : (
          <button
            type="button"
            role="menuitem"
            aria-haspopup="menu"
            className="ch-more__item ch-more__item--submenu"
            onClick={(e) => {
              const alvo = e.currentTarget
              setSubmenuSilencio((atual) => (atual ? null : alvo))
            }}
          >
            <i className="fas fa-bell-slash" aria-hidden />
            Silenciar notificações
            <i className="fas fa-chevron-right ch-more__seta" aria-hidden />
          </button>
        )}

        <button
          type="button"
          role="menuitem"
          className="ch-more__item"
          onClick={() => {
            acoes.fixar.mutate({ channelId: canal.id, fixar: !fixada }, { onError: avisarFalha })
            fechar()
          }}
        >
          <i className="fas fa-thumbtack" aria-hidden />
          {fixada ? 'Desafixar conversa' : 'Fixar conversa'}
        </button>

        {podeMarcarNaoLida ? (
          <button
            type="button"
            role="menuitem"
            className="ch-more__item"
            onClick={() => {
              acoes.marcarNaoLida.mutate(canal.id, { onError: avisarFalha })
              fechar()
            }}
          >
            <i className="fas fa-envelope" aria-hidden />
            Marcar como não lida
          </button>
        ) : null}

        <button
          type="button"
          role="menuitem"
          className="ch-more__item"
          onClick={() => {
            acoes.favoritar.mutate({ channelId: canal.id, favoritar: !favorita }, { onError: avisarFalha })
            fechar()
          }}
        >
          <i className={favorita ? 'fas fa-heart' : 'far fa-heart'} aria-hidden />
          {favorita ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
        </button>

        <span className="ch-menu__divisor" aria-hidden />

        <button
          type="button"
          role="menuitem"
          className="ch-more__item"
          onClick={() => {
            setConfirmando('limpar')
          }}
        >
          <i className="fas fa-broom" aria-hidden />
          Limpar conversa
        </button>

        {canal.kind !== 'pessoa' ? (
          <button
            type="button"
            role="menuitem"
            className="ch-more__item"
            onClick={() => {
              setConfirmando('sair')
            }}
          >
            <i className="fas fa-right-from-bracket" aria-hidden />
            Sair do grupo
          </button>
        ) : null}
      </AnchoredOverlay>

      {submenuSilencio ? (
        <AnchoredOverlay
          anchor={submenuSilencio}
          onClose={() => setSubmenuSilencio(null)}
          prefer="direita"
          role="menu"
          aria-label="Silenciar por"
          className="ch-menu"
        >
          <span className="ch-drawer__sub ch-menu__label">Silenciar por</span>
          {OPCOES_DE_SILENCIO.map((opcao) => (
            <button
              key={opcao.id}
              type="button"
              role="menuitem"
              className="ch-more__item"
              onClick={() => {
                acoes.silenciar.mutate({ channelId: canal.id, duracao: opcao.id }, { onError: avisarFalha })
                fechar()
              }}
            >
              {opcao.rotulo}
            </button>
          ))}
        </AnchoredOverlay>
      ) : null}

      <ConfirmDialog
        open={confirmando === 'limpar'}
        title="Limpar conversa"
        description="As mensagens já trocadas somem só do SEU histórico — quem mais participa continua vendo tudo normalmente."
        confirmLabel="Limpar"
        destructive
        onCancel={() => setConfirmando(null)}
        onConfirm={() => {
          acoes.limpar.mutate(canal.id, { onError: avisarFalha })
          setConfirmando(null)
          fechar()
        }}
      />

      <ConfirmDialog
        open={confirmando === 'sair'}
        title="Sair do grupo"
        description={`Você deixa de participar de "${canal.name}". As mensagens já enviadas continuam para quem ficar.`}
        confirmLabel="Sair"
        destructive
        onCancel={() => setConfirmando(null)}
        onConfirm={() => {
          acoes.sairDoGrupo.mutate(
            { channelId: canal.id, meuMemberId: euMemberId },
            {
              onError: avisarFalha,
              onSuccess: () => {
                if (estaAberta) onSairDaAberta()
              },
            },
          )
          setConfirmando(null)
          fechar()
        }}
      />
    </>
  )
}
